// Story structure templates for the Idea page outline.
//
// Structures (stage order and rough proportions) are common craft knowledge; the names, beat
// labels and guidance here are written for ezart. Positions are percentages of the idea's
// target length so a template fits any page count.

export interface TemplateBeat {
    title: string;
    guidance: string;
    lane: 'macro' | 'micro';
    start: number;   // % of target length
    length: number;  // % of target length
    color?: string;  // Beat Board fill
}

export interface StructureTemplate {
    id: string;
    name: string;
    summary: string;
    targetPages: number;  // suggested length when the outline is empty
    beats: TemplateBeat[];
}

const ACT_COLORS = ['#9AAEDB', '#63A7EF', '#AF93E8', '#E298DD', '#88CAB8'];

const act = (title: string, guidance: string, start: number, length: number, colorIndex: number): TemplateBeat =>
    ({ title, guidance, lane: 'macro', start, length, color: ACT_COLORS[colorIndex % ACT_COLORS.length] });

const beat = (title: string, guidance: string, start: number, length: number, color?: string): TemplateBeat =>
    ({ title, guidance, lane: 'micro', start, length, color });

export const STRUCTURE_TEMPLATES: StructureTemplate[] = [
    {
        id: 'three-act',
        name: 'Three-Act Structure',
        summary: 'Setup, confrontation and resolution with five key turns.',
        targetPages: 120,
        beats: [
            act('Act One — Setup', 'Who is this about, what do they want, and what is normal before everything changes?', 0, 25, 0),
            act('Act Two — Confrontation', 'The pursuit. Obstacles get harder and the cost of failing keeps rising.', 25, 50, 1),
            act('Act Three — Resolution', 'Everything converges. The central question gets its answer.', 75, 25, 2),
            beat('Inciting Incident', 'The event that disrupts normal life and makes a choice unavoidable.', 10, 2, '#EFA462'),
            beat('First Turning Point', 'The protagonist commits; there is no easy way back.', 23, 2, '#E5CB6C'),
            beat('Midpoint', 'A reversal or revelation that changes what the struggle is about.', 49, 2, '#EB627B'),
            beat('Crisis', 'The lowest point: the plan fails and the goal looks lost.', 73, 2, '#B27C73'),
            beat('Climax', 'The decisive confrontation where the protagonist acts on everything learned.', 86, 6, '#8FC36A'),
        ],
    },
    {
        id: 'four-act',
        name: 'Four-Act Structure',
        summary: 'Three acts with the long middle split at the midpoint.',
        targetPages: 120,
        beats: [
            act('Act One', 'Establish the world, the protagonist and the problem.', 0, 25, 0),
            act('Act Two A', 'Reacting: the protagonist tries the obvious approach.', 25, 25, 1),
            act('Act Two B', 'Acting: with new understanding, they push harder and lose more.', 50, 25, 2),
            act('Act Three', 'The final attempt and its consequences.', 75, 25, 3),
            beat('Inciting Incident', 'What starts the story moving.', 10, 2, '#EFA462'),
            beat('Midpoint Shift', 'The story pivots from reacting to acting.', 49, 2, '#EB627B'),
            beat('Lowest Point', 'Everything that could go wrong has.', 74, 2, '#B27C73'),
            beat('Climax', 'The final test.', 88, 5, '#8FC36A'),
        ],
    },
    {
        id: 'freytag',
        name: "Freytag's Pyramid",
        summary: 'Five-part dramatic arc rising to a central peak.',
        targetPages: 120,
        beats: [
            act('Exposition', 'The situation and the people in it, before the conflict ignites.', 0, 20, 0),
            act('Rising Action', 'Complications build and the tension tightens.', 20, 25, 1),
            act('Climax', 'The peak: the turn on which the whole story pivots.', 45, 10, 2),
            act('Falling Action', 'Consequences of the turn unfold.', 55, 25, 3),
            act('Denouement', 'Loose ends tie off and a new normal settles.', 80, 20, 4),
        ],
    },
    {
        id: 'eight-sequences',
        name: 'Eight Sequences',
        summary: 'Three acts built from eight roughly equal sequences.',
        targetPages: 120,
        beats: [
            act('Act One', 'Sequences A and B.', 0, 25, 0),
            act('Act Two', 'Sequences C through F.', 25, 50, 1),
            act('Act Three', 'Sequences G and H.', 75, 25, 2),
            beat('A — Status Quo', 'Life as it is, and the hint of what is missing.', 0, 12.5, '#9AAEDB'),
            beat('B — Locked In', 'The problem arrives and the protagonist is committed.', 12.5, 12.5, '#63A7EF'),
            beat('C — First Attempt', 'The first plan meets the first real obstacle.', 25, 12.5, '#88CAB8'),
            beat('D — Toward the Middle', 'Momentum builds to a midpoint discovery.', 37.5, 12.5, '#8FC36A'),
            beat('E — Complications', 'New information makes everything harder.', 50, 12.5, '#E5CB6C'),
            beat('F — Collapse', 'The approach fails; the lowest point.', 62.5, 12.5, '#EFA462'),
            beat('G — Final Push', 'A new plan born from failure.', 75, 12.5, '#EB627B'),
            beat('H — Resolution', 'The outcome and what it means.', 87.5, 12.5, '#AF93E8'),
        ],
    },
    {
        id: 'heros-journey',
        name: "Hero's Journey",
        summary: 'Departure into the unknown, ordeal, and return transformed.',
        targetPages: 120,
        beats: [
            act('Known World', 'Home, before the adventure.', 0, 25, 0),
            act('Unknown World', 'The adventure itself.', 25, 50, 1),
            act('Return', 'Bringing what was gained back home.', 75, 25, 2),
            beat('Everyday World', 'Ordinary life and the hero\'s unmet need.', 0, 8, '#9AAEDB'),
            beat('The Call', 'An invitation or threat that points toward change.', 8, 4, '#63A7EF'),
            beat('Hesitation', 'Fear or duty holds the hero back.', 12, 4, '#88CAB8'),
            beat('Guidance', 'Advice, a gift or an example that makes the leap possible.', 16, 5, '#8FC36A'),
            beat('Into the Unknown', 'The hero crosses into unfamiliar territory.', 21, 6, '#E5CB6C'),
            beat('Trials and Allies', 'Learning the rules, finding friends and foes.', 27, 13, '#EFA462'),
            beat('Closing In', 'Preparing to face the central danger.', 40, 9, '#E298DD'),
            beat('The Ordeal', 'The greatest test so far; something must die or change.', 49, 8, '#EB627B'),
            beat('The Prize', 'What the ordeal earned, and what it cost.', 57, 9, '#AF93E8'),
            beat('The Way Back', 'Consequences follow the hero toward home.', 66, 9, '#B27C73'),
            beat('Final Test', 'One last trial proves the change is real.', 75, 15, '#EB627B'),
            beat('Homecoming', 'Return with something that changes the world left behind.', 90, 10, '#9AAEDB'),
        ],
    },
    {
        id: 'story-circle',
        name: 'Story Circle',
        summary: 'Eight steps around a circle: leave comfort, pay for change, come home different.',
        targetPages: 120,
        beats: [
            act('Order', 'The familiar world.', 0, 25, 0),
            act('Chaos', 'The unfamiliar situation.', 25, 50, 1),
            act('Order Restored', 'Back to the familiar, changed.', 75, 25, 2),
            beat('Comfort', 'A character in a zone of comfort.', 0, 12.5, '#9AAEDB'),
            beat('Desire', 'They want something.', 12.5, 12.5, '#63A7EF'),
            beat('Departure', 'They enter an unfamiliar situation.', 25, 12.5, '#88CAB8'),
            beat('Adapting', 'They adapt to it.', 37.5, 12.5, '#8FC36A'),
            beat('Getting It', 'They get what they wanted.', 50, 12.5, '#E5CB6C'),
            beat('Paying the Price', 'It costs more than expected.', 62.5, 12.5, '#EFA462'),
            beat('Coming Back', 'They return to the familiar.', 75, 12.5, '#EB627B'),
            beat('Changed', 'Having changed.', 87.5, 12.5, '#AF93E8'),
        ],
    },
    {
        id: 'outer-inner',
        name: 'Outer & Inner Journey',
        summary: 'A visible goal on the outside, an emotional arc on the inside, five turning points.',
        targetPages: 120,
        beats: [
            act('Everyday Life', 'The outer situation, and the protective identity the hero hides behind.', 0, 10, 0),
            act('New Circumstances', 'A new situation; the hero still plays it safe inside.', 10, 15, 1),
            act('Making Progress', 'The plan works; glimpses of the hero\'s truer self.', 25, 25, 2),
            act('Rising Stakes', 'Harder obstacles; the old identity starts to crack.', 50, 25, 3),
            act('Last Stand', 'Everything on the line, inside and out.', 75, 15, 4),
            act('After', 'The new life, and who the hero has become.', 90, 10, 0),
            beat('An Opening', 'Something new becomes possible.', 10, 2, '#EFA462'),
            beat('New Goal', 'A concrete, visible goal takes shape.', 25, 2, '#E5CB6C'),
            beat('Point of Commitment', 'The hero burns a bridge.', 50, 2, '#EB627B'),
            beat('Biggest Setback', 'The goal seems impossible.', 75, 2, '#B27C73'),
            beat('Showdown', 'Facing the biggest obstacle, outside and in.', 90, 5, '#8FC36A'),
        ],
    },
    {
        id: 'fifteen-beats',
        name: 'Fifteen Beats',
        summary: 'A detailed beat sheet for commercial feature pacing.',
        targetPages: 110,
        beats: [
            act('Beginning', 'Opening picture through commitment.', 0, 20, 0),
            act('Middle', 'The promise of the premise, then the squeeze.', 20, 60, 1),
            act('End', 'New resolve through the closing picture.', 80, 20, 2),
            beat('Opening Picture', 'A snapshot of the world and the hero before the story.', 0, 1, '#9AAEDB'),
            beat('The Question', 'Someone voices what the story is really about.', 4.5, 1, '#63A7EF'),
            beat('Ordinary Life', 'The hero\'s life, flaws and what is missing.', 1, 9, '#88CAB8'),
            beat('The Spark', 'The event that changes everything.', 10, 1, '#EFA462'),
            beat('Weighing It', 'Should they go? What is at risk?', 11, 9, '#E5CB6C'),
            beat('Commitment', 'The hero chooses to step into the new world.', 20, 2, '#8FC36A'),
            beat('Second Thread', 'A relationship that carries the theme.', 22, 3, '#E298DD'),
            beat('The Promise', 'The fun and games the premise promised.', 20, 30, '#63A7EF'),
            beat('Midpoint Shift', 'A false victory or false defeat raises the stakes.', 50, 2, '#EB627B'),
            beat('Pressure Builds', 'Outside forces and inner doubts close in.', 52, 23, '#B27C73'),
            beat('Rock Bottom', 'The moment that looks like total defeat.', 75, 2, '#B27C73'),
            beat('Darkest Hour', 'Sitting with the loss.', 77, 4, '#AF93E8'),
            beat('New Resolve', 'An insight shows the way forward.', 81, 2, '#8FC36A'),
            beat('Final Act', 'The new plan is executed and the hero proves the change.', 83, 16, '#EFA462'),
            beat('Closing Picture', 'The opposite of the opening picture.', 99, 1, '#9AAEDB'),
        ],
    },
    {
        id: 'kishotenketsu',
        name: 'Kishōtenketsu',
        summary: 'Four parts built on contrast and a twist rather than conflict.',
        targetPages: 100,
        beats: [
            act('Introduction', 'Present the characters and their world.', 0, 25, 0),
            act('Development', 'Deepen what was introduced; nothing breaks yet.', 25, 25, 1),
            act('Twist', 'Something unexpected that recasts everything so far.', 50, 25, 2),
            act('Reconciliation', 'Bring the parts together into a new understanding.', 75, 25, 3),
        ],
    },
    {
        id: 'tv-one-hour',
        name: 'TV: Teaser + Four Acts',
        summary: 'One-hour drama with a cold-open teaser and act-out cliffhangers.',
        targetPages: 60,
        beats: [
            act('Teaser', 'Hook the audience before the titles.', 0, 6, 0),
            act('Act One', 'Set up the episode\'s problem.', 6, 23, 1),
            act('Act Two', 'Complications and a bigger question.', 29, 24, 2),
            act('Act Three', 'Reversals; things get worse.', 53, 24, 3),
            act('Act Four', 'Resolution, and a hook for next time.', 77, 23, 4),
            beat('Act Out', 'End Act One on a question that carries through the break.', 27, 2, '#EFA462'),
            beat('Act Out', 'End Act Two on a reversal.', 51, 2, '#EFA462'),
            beat('Act Out', 'End Act Three on the biggest cliffhanger.', 75, 2, '#EB627B'),
        ],
    },
    {
        id: 'tv-half-hour',
        name: 'TV: Half-Hour',
        summary: 'Cold open, two acts and a tag.',
        targetPages: 30,
        beats: [
            act('Cold Open', 'A self-contained bit that sets the tone.', 0, 10, 0),
            act('Act One', 'The A-story problem and B-story setup.', 10, 40, 1),
            act('Act Two', 'Escalation, collision and resolution.', 50, 43, 2),
            act('Tag', 'A final button after the story ends.', 93, 7, 3),
        ],
    },
];
